import {DatabaseCode, toDatabaseCode, toDatabaseCodeSafely} from "./database-code";
import {PatchValue} from "./patch-value";

export type AttachmentTypeCode =
  | "TICKET"
  | "TANK_LEVEL"
  | "REGULATORY_FORM"
  | "COMPANY_LOGO"
  | "SIGNATURE"
  | "REPORT"
  | "FILE";

export type AttachmentType = DatabaseCode & {code: AttachmentTypeCode};

const defineAttachmentType = (code: AttachmentTypeCode, defaultMessage: string): AttachmentType => ({
  code,
  displayValue: {id: `common.entity.attachmenttype.${code.toLowerCase()}`, defaultMessage},
});

export const attachmentType = {
  ticket: defineAttachmentType("TICKET", "Ticket"),
  tankLevel: defineAttachmentType("TANK_LEVEL", "Container Level"),
  regulatoryForm: defineAttachmentType("REGULATORY_FORM", "Regulatory Form"),
  companyLogo: defineAttachmentType("COMPANY_LOGO", "Company Logo"),
  signature: defineAttachmentType("SIGNATURE", "Signature"),
  report: defineAttachmentType("REPORT", "Report"),
  file: defineAttachmentType("FILE", "File"),
} as const;

export const attachmentTypes: AttachmentType[] = Object.values(attachmentType);

export const toAttachmentType = (code: string): AttachmentType =>
  toDatabaseCode(code, attachmentTypes, attachmentType.file) as AttachmentType;

export const toAttachmentTypeSafely = (code: string | undefined): AttachmentType | undefined =>
  toDatabaseCodeSafely(code, attachmentTypes) as AttachmentType | undefined;

export type AttachmentTarget = {
  objectId: string;
  objectType: string;
  /** Human-readable context such as "Ticket 12345". */
  label?: string;
  tenantCode?: string;
};

export type AttachmentCapabilities = {
  canView: boolean;
  canUpload: boolean;
  canEdit: boolean;
  canDelete: boolean;
  allowedAttachmentTypes?: AttachmentTypeCode[];
  allowedContentTypes?: string[];
  maxFileBytes?: number;
  maxFilesPerUpload?: number;
};

export type AttachmentFile = {
  id: string;
  objectId: string;
  objectType: string;
  attachmentType: AttachmentTypeCode;
  description?: string | null;
  displayOrder?: number | null;
  reportable?: boolean | null;
  fileType?: string | null;
  fileName: string;
  contentLength?: number | null;
  contentType?: string | null;
  checksumSha256?: string | null;
  createdById?: string | null;
  lastModifiedById?: string | null;
  /** Epoch milliseconds, matching the platform entity contract. */
  createDttm?: number | null;
  /** Epoch milliseconds, matching the platform entity contract. */
  lastModifiedDttm?: number | null;
  downloadUrl?: string | null;
};

export type AttachmentBundle = {
  attachments: AttachmentFile[];
  bulkDownloadUrl?: string | null;
  hasAttachments: boolean;
  attachmentCount: number;
  /** Optional until the attachment service publishes ABAC decisions with the resource. */
  capabilities?: AttachmentCapabilities;
};

export type AttachmentUploadMetadata = {
  attachmentType: AttachmentTypeCode;
  description?: string;
  displayOrder?: number;
  reportable?: boolean;
};

export type AttachmentUploadItem = {
  clientId?: string;
  target: AttachmentTarget;
  file: File;
  metadata: AttachmentUploadMetadata;
};

export type AttachmentUpdateRequest = {
  attachment: AttachmentFile;
  changes: Pick<AttachmentFile, "attachmentType" | "description" | "displayOrder">;
};

export type AttachmentMetadataPatch = {
  attachmentType?: PatchValue<AttachmentTypeCode>;
  description?: PatchValue<string | null>;
  displayOrder?: PatchValue<number | null>;
};
